import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';


const NewsCard = ({ event, index, compact = false, featured = false, className = '', layout = 'overlay' }) => {
    // Determine image loading priority
    const isPriority = index === 0 || featured;

    // CARD LAYOUT (Mobile Reference Style)
    if (layout === 'card') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className={`group flex flex-col bg-white rounded-3xl overflow-hidden border border-white/10 shadow-2xl ${className}`}
            >
                {/* Image Section */}
                <div className="relative w-full h-60 md:h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors z-10" />

                    {/* Date Pill (Top Right) */}
                    <div className="absolute top-4 right-4 z-20 bg-white text-slate-900 px-4 py-1.5 rounded-full font-bold text-sm tracking-wide shadow-lg border border-slate-100">
                        {event.date}
                    </div>

                    <Link to={`/haberler/${event.id}`}>
                        <img
                            src={event.image}
                            alt={event.title}
                            loading={isPriority ? "eager" : "lazy"}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </Link>
                </div>

                {/* Content Section */}
                <div className="p-6 flex flex-col gap-4 flex-1">
                    <Link to={`/haberler/${event.id}`} className="block">
                        <h3 className="text-xl md:text-2xl font-bold text-[#0f172a] leading-tight group-hover:text-[#025159] transition-colors line-clamp-3">
                            {event.title}
                        </h3>
                    </Link>

                    <p className="text-slate-600 text-sm md:text-base leading-relaxed line-clamp-3">
                        {event.description || "Etkinlik detaylarını incelemek için tıklayınız."}
                    </p>

                    <Link
                        to={`/haberler/${event.id}`}
                        className="mt-auto inline-flex items-center gap-2 text-[#0891b2] text-sm md:text-base font-bold hover:text-[#0e7490] transition-colors"
                    >
                        Detayları İncele
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </motion.div>
        );
    }

    // OVERLAY LAYOUT (Default / Desktop Slider)
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: (index || 0) * 0.1, duration: 0.5 }}
            viewport={{ once: true }}
            className={`group relative bg-[#0a0a0a] rounded-2xl overflow-hidden border border-white/5 hover:border-cyan-500/30 transition-all duration-500 flex flex-col ${featured ? 'h-full min-h-[400px]' : (compact ? 'h-[300px]' : 'h-[380px]')} ${className}`}
        >
            {/* Clickable Overlay */}
            <Link to={`/haberler/${event.id}`} className="absolute inset-0 z-40" aria-label="Haberi Oku" />

            {/* Full Height Image Container */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent z-10" />

                {/* Background Blur for Contain Mode */}
                {event.imageFit === 'contain' && (
                    <div
                        className="absolute inset-0 bg-cover bg-center blur-md scale-110 opacity-50"
                        style={{ backgroundImage: `url(${event.image})` }}
                    />
                )}

                <motion.img
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.7 }}
                    src={event.image}
                    alt={event.title}
                    loading={isPriority ? "eager" : "lazy"}
                    className={`w-full h-full ${event.imageFit === 'contain' ? 'object-contain' : 'object-cover'} ${event.imagePosition || 'object-center'}`}
                />
            </div>

            {/* NEW Date Box Design - Full Date */}
            <div className="absolute top-4 right-4 z-20 bg-white/95 backdrop-blur-md rounded-lg px-4 py-2 shadow-xl border border-white/50 group-hover:scale-105 transition-transform duration-300 pointer-events-none">
                <span className="text-sm md:text-base font-bold text-black tracking-tight">
                    {event.date}
                </span>
            </div>

            {/* Content Overlay (Bottom) */}
            <div className={`absolute bottom-0 left-0 right-0 z-20 flex flex-col justify-end h-full pointer-events-none ${featured ? 'p-6 pb-10 md:p-8 md:pb-12' : 'p-6'}`}>
                <div className="pointer-events-auto block">
                    {/* Title - Simplified (No Box/Blur) */}
                    <h3 className={`inline ${featured ? 'text-xl md:text-3xl' : 'text-lg md:text-2xl'} font-bold text-white leading-tight drop-shadow-xl font-heading`}>
                        {event.title}
                    </h3>

                    {/* Read More Link (Visual Only) */}
                    <span
                        className="inline-flex items-center gap-2 text-white/90 text-sm font-bold bg-black/40 backdrop-blur-sm px-3 py-1 rounded-full hover:bg-cyan-600/90 hover:text-white transition-all group-hover:translate-x-1 duration-300 border border-white/10 ml-3 align-middle my-1"
                    >
                        Devamını Oku
                        <ArrowRight className="w-4 h-4" />
                    </span>
                </div>
            </div>

            {/* Shine Effect */}
            <div className="absolute inset-0 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute inset-y-0 left-0 w-[1px] bg-gradient-to-b from-transparent via-cyan-500/50 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-1000" />
                <div className="absolute inset-y-0 right-0 w-[1px] bg-gradient-to-b from-transparent via-cyan-500/50 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-1000 delay-100" />
            </div>
        </motion.div>
    );
};

export default NewsCard;

